import type { PanInfo } from 'framer-motion';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  ArrowLeft,
  Bookmark,
  Briefcase,
  CheckCircle2,
  DollarSign,
  ExternalLink,
  Lightbulb,
  MapPin,
  RefreshCw,
  Sparkles,
  Timer,
  X,
  XCircle,
  Zap,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';

import LoadingSpinner from '../../../../components/LoadingSpinner';
import SalaryIntelligenceCard from '../../../../components/Jobs/SalaryIntelligenceCard';
import ShouldIApplyPanel from '../../../../components/Jobs/ShouldIApplyPanel';
import { supabase } from '../../../../lib/supabase';
import { computeSalaryIntelligenceForJob } from '../../../../services/salaryIntelligenceService';
import type { Job } from '../JobCard';
import { useAnonymizedJob } from '../../hooks/useAnonymizedJob';
import { useApplyClickLogger } from '../../hooks/useApplyClickLogger';
import { useUserProfile } from '../../hooks/useUserProfile';
import type { ExplanationStatus, JobMatchExplanation } from '../../hooks/useJobMatchExplanation';
import { useJobMatchExplanation } from '../../hooks/useJobMatchExplanation';

import type { MarkdownSection } from './helpers';
import { freshnessLabel, localizeSectionTitle, splitMarkdownSections } from './helpers';
import { useJobContentTranslation } from '../../hooks/useJobContentTranslation';
import PeerlistIcon from './PeerlistIcon';
import SimilarJobs from './SimilarJobs';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface JobDetailDrawerProps {
  job: Job | null;
  onClose: () => void;
  onApply: (job: Job) => void;
  onView: (job: Job) => void;
  onToggleSave: (job: Job) => void;
  isSaved: boolean;
  /** Full list of jobs passed from Jobs.tsx — enables the Similar jobs section. */
  allJobs?: Job[];
  /** Called when the user clicks a similar job row; Jobs.tsx swaps selectedJob. */
  onSelectSimilar?: (job: Job) => void;
  /** Optimized / Applied status from agent or My Activities. */
  statusBadge?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DRAG_CLOSE_THRESHOLD = 120;
const MOBILE_BP = 640;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatSalary(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
      notation: 'compact',
    }).format(value);
  } catch {
    return `${currency} ${value.toLocaleString()}`;
  }
}

function buildSalaryLabel(
  min: number | undefined,
  max: number | undefined,
  currency: string,
  t: (key: string, defaultValue: string, options?: Record<string, unknown>) => string,
): string {
  if (min != null && max != null)
    return `${formatSalary(min, currency)} – ${formatSalary(max, currency)}`;
  if (min != null) return t('dashboard.jobs.jobCard.salaryFrom', { defaultValue: 'From {{amount}}', amount: formatSalary(min, currency) });
  if (max != null) return t('dashboard.jobs.jobCard.salaryUpTo', { defaultValue: 'Up to {{amount}}', amount: formatSalary(max, currency) });
  return '';
}

/**
 * When the stored description is plain text with no markdown structure
 * (older rows parsed before we taught the LLM to preserve headings), split
 * it into readable paragraphs so the drawer doesn't render one monolithic
 * bold-looking wall of text.
 */
function normalizeDescription(md: string | undefined): string {
  if (!md) return '';
  const trimmed = md.trim();
  if (!trimmed) return '';

  // Already has markdown structure — leave it alone.
  const hasStructure =
    /(^|\n)\s{0,3}#{1,6}\s/.test(trimmed) ||
    /(^|\n)\s{0,3}[-*+]\s/.test(trimmed) ||
    /\n\s*\n/.test(trimmed);

  if (hasStructure) return trimmed;

  /**
   * Plain-text job descriptions often arrive as one long run-on string with no
   * periods — requirements are just concatenated:
   *   "3+ years experience in X Strong proficiency in Y Experience with Z..."
   *
   * Strategy (two passes):
   *   1. Split on traditional sentence endings: ". " / "! " / "? " + capital.
   *   2. For each chunk that still has no period, split on the pattern
   *      "lowercase|comma|) + space + Capital-word (≥3 lowercase chars following)"
   *      which catches run-on requirement transitions reliably.
   * Then emit every item as a `- bullet` for ReactMarkdown.
   */
  const byPeriod = trimmed.split(/(?<=[.!?])\s+(?=[A-Z\d])/);

  const items = byPeriod
    .flatMap((chunk) =>
      // Only split run-on chunks — if the chunk already has a period it's a
      // proper sentence and should stay whole.
      chunk.includes('.')
        ? [chunk]
        : chunk.split(/(?<=[a-z,)%])\s+(?=[A-Z][a-z]{2,})/)
    )
    .map((s) => s.trim())
    .filter((s) => s.length > 8);   // drop tiny fragments

  // If we produced more than one item, render as a bullet list.
  if (items.length > 1) {
    return items.map((s) => `- ${s}`).join('\n');
  }

  // Single sentence — return as a plain paragraph (no bullets).
  return trimmed;
}

function useIsMobile() {
  const [mobile, setMobile] = React.useState(
    () => typeof window !== 'undefined' && window.innerWidth < MOBILE_BP,
  );
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BP - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return mobile;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const CORE_SKILL_COUNT = 5;

interface MetaPillProps {
  icon: React.ReactNode;
  label: string;
}

const MetaPill: React.FC<MetaPillProps> = ({ icon, label }) => (
  <div className="ds-job-drawer-meta-pill" title={label}>
    <span className="ds-job-drawer-meta-pill__icon" aria-hidden="true">{icon}</span>
    <span className="ds-job-drawer-meta-pill__label">{label}</span>
  </div>
);

interface CollapsibleSkillGroupProps {
  title: string;
  skills: string[];
  matchedSkills?: string[];
}

const CollapsibleSkillGroup: React.FC<CollapsibleSkillGroupProps> = ({
  title,
  skills,
  matchedSkills = [],
}) => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const matchedSet = useMemo(
    () => new Set(matchedSkills.map((s) => s.trim().toLowerCase())),
    [matchedSkills],
  );

  const isMatched = useCallback(
    (skill: string) => matchedSet.has(skill.trim().toLowerCase()),
    [matchedSet],
  );

  if (!skills || skills.length === 0) return null;

  const core = skills.slice(0, CORE_SKILL_COUNT);
  const secondary = skills.slice(CORE_SKILL_COUNT);

  const renderSkill = (skill: string) => {
    const matched = isMatched(skill);
    return (
      <span
        key={skill}
        className={[
          'ds-job-drawer-skill',
          matched ? 'ds-job-drawer-skill--matched' : 'ds-job-drawer-skill--missing',
        ].join(' ')}
        title={skill}
      >
        {matched && <CheckCircle2 className="w-3 h-3 shrink-0 opacity-80" aria-hidden />}
        {skill}
      </span>
    );
  };

  return (
    <div className="ds-card ds-job-drawer-panel ds-job-drawer-skills">
      <h3 className="t-card-title mb-2">{title}</h3>
      <p className="t-tag t-muted mb-1.5">{t('dashboard.jobs.jobDrawer.coreSkills', 'Core')}</p>
      <div className="flex flex-wrap gap-1.5">{core.map(renderSkill)}</div>
      {secondary.length > 0 && (
        <div className="mt-3">
          {!expanded && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="ds-job-drawer-skills__toggle"
              aria-expanded={false}
            >
              {t('dashboard.jobs.jobDrawer.moreSkills', { defaultValue: '+{{count}} more', count: secondary.length })}
            </button>
          )}
          <motion.div
            className="ds-job-drawer-skills__expand"
            initial={false}
            animate={{
              height: expanded ? 'auto' : 0,
              opacity: expanded ? 1 : 0,
              marginTop: expanded ? 12 : 0,
            }}
            transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
            style={{ overflow: 'hidden' }}
            aria-hidden={!expanded}
          >
            <p className="t-tag t-muted mb-1.5">{t('dashboard.jobs.jobDrawer.secondarySkills', 'Secondary')}</p>
            <div className="flex flex-wrap gap-1.5">{secondary.map(renderSkill)}</div>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="ds-job-drawer-skills__toggle mt-2"
              aria-expanded={true}
            >
              {t('dashboard.jobs.jobDrawer.showLess', 'Show less')}
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Markdown prose styling — shared by every Section so typography stays
// consistent (no more bold-looking blobs).
// ---------------------------------------------------------------------------

const PROSE_CLASSES = [
  'max-w-none break-words t-body',
  '[&_h1]:text-base [&_h1]:font-semibold [&_h1]:text-[var(--ds-text-strong)] [&_h1]:mt-5 [&_h1]:mb-2',
  '[&_h2]:text-sm [&_h2]:font-semibold [&_h2]:text-[var(--ds-text-strong)] [&_h2]:mt-5 [&_h2]:mb-2',
  '[&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-[var(--ds-text-strong)] [&_h3]:mt-4 [&_h3]:mb-2',
  '[&_h4]:text-sm [&_h4]:font-semibold [&_h4]:text-[var(--ds-text-strong)] [&_h4]:mt-3 [&_h4]:mb-1.5',
  '[&_p]:text-sm [&_p]:leading-relaxed [&_p]:mb-3 [&_p]:font-normal [&_p]:text-[var(--ds-text-body)]',
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ul]:space-y-2',
  '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_ol]:space-y-2',
  '[&_li]:text-sm [&_li]:leading-relaxed [&_li]:font-normal [&_li]:marker:text-[var(--ds-brand)]',
  '[&_a]:text-[var(--ds-text-link)] [&_a]:underline [&_a]:underline-offset-2',
  '[&_strong]:font-semibold [&_strong]:text-[var(--ds-text-strong)]',
  '[&_em]:italic',
  '[&_hr]:my-4 [&_hr]:border-t [&_hr]:border-[var(--ds-border)]',
  '[&_code]:text-xs [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:bg-[var(--ds-bg-tinted)] [&_code]:text-[var(--ds-text-body)]',
].join(' ');

// ---------------------------------------------------------------------------
// Section sub-component — renders a titled markdown block in a subtle card.
// ---------------------------------------------------------------------------

interface SectionProps {
  title: string;
  body: string;
  /** Sub-sections nest as h4 (no card background) */
  nested?: boolean;
}

const Section: React.FC<SectionProps> = ({ title, body, nested = false }) => {
  if (!body.trim() && !title.trim()) return null;
  return (
    <section
      className={
        nested
          ? 'pl-3 border-l-2 border-[var(--ds-brand-border)]'
          : 'ds-card ds-job-drawer-panel'
      }
    >
      {title && (
        <h4
          className={
            nested
              ? 't-card-title mb-2'
              : 't-h3 mb-3'
          }
        >
          {title}
        </h4>
      )}
      {body && (
        <div className={PROSE_CLASSES}>
          <ReactMarkdown>{body}</ReactMarkdown>
        </div>
      )}
    </section>
  );
};

interface CompanyAvatarProps {
  url?: string;
  company: string;
}

const CompanyAvatar: React.FC<CompanyAvatarProps> = ({ url, company }) => {
  const [failed, setFailed] = React.useState(false);
  const initial = (company.trim()[0] ?? '?').toUpperCase();
  if (!url || failed) {
    return (
      <div
        className="ds-icon-square ds-icon-square-lg t-card-title shrink-0"
        aria-hidden="true"
      >
        {initial}
      </div>
    );
  }
  return (
    <img
      src={url}
      alt=""
      aria-hidden="true"
      className="w-12 h-12 rounded-2xl object-contain shrink-0"
      style={{ background: 'var(--ds-bg-card)' }}
      onError={() => setFailed(true)}
    />
  );
};

// ---------------------------------------------------------------------------
// useUserId — reads the current session user_id without adding a prop.
// One getSession call per drawer open; result is stable while the drawer
// is open and re-resolves if the auth state changes.
// ---------------------------------------------------------------------------

function useUserId(): string | null {
  const [userId, setUserId] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (active) setUserId(session?.user?.id ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      if (active) setUserId(session?.user?.id ?? null);
    });
    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);
  return userId;
}

// ---------------------------------------------------------------------------
// Band pill helpers
// ---------------------------------------------------------------------------

const getBandLabel = (
  key: string,
  t: (k: string, defaultValue: string) => string,
): string => {
  const labels: Record<string, string> = {
    strong_fit: t('dashboard.jobs.jobDrawer.bandStrongFit', 'Strong fit'),
    good_fit: t('dashboard.jobs.jobDrawer.bandGoodFit', 'Good fit'),
    partial_fit: t('dashboard.jobs.jobDrawer.bandPartialFit', 'Partial fit'),
    not_recommended: t('dashboard.jobs.jobDrawer.bandNotRecommended', 'Not recommended'),
  };
  return labels[key] ?? labels.partial_fit!;
};

const BAND_CONFIG: Record<
  string,
  { bg: string; text: string; icon: React.ReactNode }
> = {
  strong_fit:       { bg: 'ds-chip-ok',     text: '', icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
  good_fit:         { bg: 'ds-chip-warn',   text: '', icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
  partial_fit:      { bg: 'ds-chip',        text: '', icon: <AlertCircle   className="w-3.5 h-3.5" /> },
  not_recommended:  { bg: 'ds-chip-danger', text: '', icon: <XCircle       className="w-3.5 h-3.5" /> },
};

// ---------------------------------------------------------------------------
// WhyThisJob — async panel, loaded below the fold.
// Renders skeleton while loading; never blocks the drawer render.
// ---------------------------------------------------------------------------

interface WhyThisJobProps {
  jobId: string;
  /** Card-level match score (0-100) from the ranking engine — used as the authoritative band/ring. */
  matchScore?: number;
  explanationStatus?: ExplanationStatus;
  data?: JobMatchExplanation | null;
  refresh?: () => void;
}

/* Bullet list item — replaces coloured oval pills */
const SkillBullet: React.FC<{ label: string; variant: 'matched' | 'missing' | 'bonus' }> = ({ label, variant }) => {
  const dot =
    variant === 'matched' ? 'bg-[var(--ds-ok)]' :
    variant === 'bonus'   ? 'bg-[var(--ds-info)]'  :
    'bg-[var(--ds-neutral)]';
  return (
    <li className="t-meta flex items-start gap-2">
      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />
      {label}
    </li>
  );
};

const Skeleton: React.FC = () => (
  <div className="animate-pulse space-y-3">
    <div className="h-3 bg-[var(--ds-bg-tinted)] rounded w-3/4" />
    <div className="h-3 bg-[var(--ds-bg-tinted)] rounded w-full" />
    <div className="h-3 bg-[var(--ds-bg-tinted)] rounded w-5/6" />
    <div className="flex gap-2 pt-1">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-5 w-16 bg-[var(--ds-bg-tinted)] rounded-full" />
      ))}
    </div>
  </div>
);

const ScoreRing: React.FC<{ score: number }> = ({ score }) => {
  const pct = Math.max(0, Math.min(100, score));
  const r = 18;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  const color = pct >= 75 ? 'var(--ds-ok)' : pct >= 55 ? 'var(--ds-warn)' : pct >= 35 ? 'var(--ds-brand)' : 'var(--ds-danger)';
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" className="shrink-0" aria-hidden="true">
      <circle cx="24" cy="24" r={r} fill="none" stroke="currentColor" strokeWidth="3" className="text-[var(--ds-bg-tinted)]" />
      <circle
        cx="24" cy="24" r={r} fill="none"
        stroke={color} strokeWidth="3"
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
        transform="rotate(-90 24 24)"
      />
      <text x="24" y="29" textAnchor="middle" fontSize="12" fontWeight="700" fill={color}>{pct}</text>
    </svg>
  );
};

const ExplanationCard: React.FC<{ data: JobMatchExplanation; matchScore?: number }> = ({ data, matchScore }) => {
  const { t, i18n } = useTranslation();
  const displayScore = matchScore != null && matchScore > 0 ? Math.round(matchScore) : data.score;
  const displayBandKey =
    displayScore >= 75 ? 'strong_fit' :
    displayScore >= 55 ? 'good_fit'   :
    displayScore >= 35 ? 'partial_fit': 'not_recommended';
  const band = BAND_CONFIG[displayBandKey] ?? BAND_CONFIG['partial_fit']!;

  /* Is the skill_gaps[0] prose substantially different from missing_skills?
     If missing_skills exist as individual items, skip the prose to avoid duplication. */
  const showGapsProse =
    data.skill_gaps.length > 0 &&
    typeof data.skill_gaps[0] === 'string' &&
    data.skill_gaps[0].length > 60 &&
    data.missing_skills.length === 0;

  return (
    <div className="space-y-4">
      {/* Score ring + band badge */}
      <div className="flex items-center gap-3">
        <ScoreRing score={displayScore} />
        <div className="min-w-0">
          <div className={`ds-chip ${band.bg} ${band.text}`}>
            {band.icon}
            <span>{getBandLabel(displayBandKey, t)}</span>
          </div>
          <p className="t-meta mt-1">
            {t('dashboard.jobs.jobDrawer.matchBasedOn', 'Based on your skills, experience & location.')}
          </p>
        </div>
      </div>

      {/* ── Why you're a match ── */}
      {data.why_matched && (
        <div className="ds-job-drawer-match-insight">
          <div className="ds-job-drawer-match-insight__header">
            <Lightbulb className="w-4 h-4 shrink-0" aria-hidden />
            <span>{t('dashboard.jobs.jobDrawer.whyMatch', "Why you're a match")}</span>
          </div>
          <p className="t-body">{data.why_matched}</p>
        </div>
      )}

      {/* ── Skills you have ── */}
      {data.matched_skills.length > 0 && (
        <div className="ds-recessed-ok">
          <p className="t-label mb-2" style={{ color: 'inherit' }}>
            {t('dashboard.jobs.jobDrawer.skillsYouHave', 'Skills you have')}
          </p>
          <ul className="space-y-1">
            {data.matched_skills.slice(0, 8).map((s) => (
              <SkillBullet key={s} label={s} variant="matched" />
            ))}
          </ul>
        </div>
      )}

      {/* ── Skill gaps ── bullet list, no red colour */}
      {data.missing_skills.length > 0 && (
        <div className="ds-job-drawer-gaps ds-recessed">
          <p className="ds-job-drawer-section-heading t-label mb-2">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" aria-hidden />
            {t('dashboard.jobs.jobDrawer.skillGaps', 'Skill gaps')}
          </p>
          <ul className="space-y-1">
            {data.missing_skills.slice(0, 8).map((s) => (
              <SkillBullet key={s} label={s} variant="missing" />
            ))}
          </ul>
        </div>
      )}

      {/* ── Bonus skills ── */}
      {data.bonus_skills.length > 0 && (
        <div className="ds-recessed-info">
          <p className="t-label mb-2" style={{ color: 'inherit' }}>
            {t('dashboard.jobs.jobDrawer.bonusSkills', 'Bonus skills you bring')}
          </p>
          <ul className="space-y-1">
            {data.bonus_skills.slice(0, 5).map((s) => (
              <SkillBullet key={s} label={s} variant="bonus" />
            ))}
          </ul>
        </div>
      )}

      {/* ── Gap analysis — only when no per-skill items to avoid duplication ── */}
      {showGapsProse && (
        <div className="ds-recessed">
          <p className="t-label mb-2">
            {t('dashboard.jobs.jobDrawer.gapAnalysis', 'Gap analysis')}
          </p>
          <p className="t-body">
            {data.skill_gaps[0]}
          </p>
        </div>
      )}

      {/* ── Next steps — vertical timeline ── */}
      {data.action_items.length > 0 && (
        <div className="ds-job-drawer-next-steps">
          <p className="t-label mb-3">{t('dashboard.jobs.jobDrawer.nextSteps', 'Next steps')}</p>
          <ol className="ds-job-drawer-steps">
            {data.action_items.map((item, i) => (
              <li key={i} className="ds-job-drawer-steps__item">
                <span className="ds-job-drawer-steps__num" aria-hidden="true">{i + 1}</span>
                <span className="t-meta">{item}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

const WhyThisJob: React.FC<WhyThisJobProps> = ({
  jobId,
  matchScore,
  explanationStatus: statusProp,
  data: dataProp,
  refresh: refreshProp,
}) => {
  const { t, i18n } = useTranslation();
  const userId = useUserId();
  const internal = useJobMatchExplanation(
    statusProp === undefined ? userId : null,
    statusProp === undefined ? jobId : null,
  );
  const status = statusProp ?? internal.status;
  const data = dataProp ?? internal.data;
  const refresh = refreshProp ?? internal.refresh;

  return (
    <section className="ds-card ds-job-drawer-panel space-y-3">
      {/* Header row */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 shrink-0" style={{ color: 'var(--ds-brand)' }} />
          <h3 className="t-h3">{t('dashboard.jobs.jobDrawer.whyThisJob', 'Why this job for you?')}</h3>
        </div>
        {(status === 'ready' || status === 'error') && (
          <button
            type="button"
            onClick={refresh}
            aria-label={t('dashboard.jobs.jobDrawer.refreshMatch', 'Refresh match explanation')}
            className="ds-btn ds-btn-ghost ds-btn-sm shrink-0"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Body states */}
      {status === 'idle' ? (
        <Skeleton />
      ) : status === 'loading' ? (
        <div className="flex flex-col items-center gap-3 py-5">
          <div
            className="relative flex items-center justify-center w-12 h-12 rounded-full"
            style={{ background: 'var(--ds-bg-tinted)' }}
          >
            <LoadingSpinner size="sm" showText={false} />
          </div>
          <div className="text-center">
            <p className="t-card-title">{t('dashboard.jobs.jobDrawer.evaluatingMatch', 'Evaluating your match…')}</p>
            <p className="t-meta mt-0.5">{t('dashboard.jobs.jobDrawer.analysingFit', 'Analysing skills, experience & fit')}</p>
          </div>
        </div>
      ) : status === 'no_profile' ? (
        <div className="space-y-2">
          <p className="t-body">
            {t('dashboard.jobs.jobDrawer.noProfileIndexed', "Your profile hasn't been indexed for semantic matching yet.")}
          </p>
          <p className="t-meta">
            {t('dashboard.jobs.jobDrawer.completeProfileHint', 'Complete your skills and experience in your profile, then click the refresh button to compute your match score.')}
          </p>
          <a
            href="/profile"
            onClick={(e) => { e.preventDefault(); window.history.pushState({}, '', '/profile'); window.dispatchEvent(new PopStateEvent('popstate')); }}
            className="t-link t-meta inline-block mt-1"
          >
            {t('dashboard.jobs.jobDrawer.goToProfile', 'Go to Profile →')}
          </a>
        </div>
      ) : status === 'error' ? (
        <p className="t-meta">
          {t('dashboard.jobs.jobDrawer.matchLoadFailed', "Couldn't load match insights right now.")}{' '}
          <button
            type="button"
            onClick={refresh}
            className="t-link transition-colors"
          >
            {t('dashboard.jobs.jobDrawer.tryAgain', 'Try again')}
          </button>
        </p>
      ) : data ? (
        <ExplanationCard data={data} matchScore={matchScore} />
      ) : null}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// Stable sentinel used when job === null so useAnonymizedJob is never called
// conditionally (React hooks must not be called conditionally).
const NULL_JOB: Job = {
  id: '',
  source: 'peerlist',
  title: '',
  company: '',
  location: '',
  url: '',
  created: '',
};

const JobDetailDrawer: React.FC<JobDetailDrawerProps> = ({
  job: rawJob,
  onClose,
  onApply,
  onView,
  onToggleSave,
  isSaved,
  allJobs = [],
  onSelectSimilar,
  statusBadge,
}) => {
  const { t, i18n } = useTranslation();
  const { profile } = useUserProfile();
  const userId = useUserId();
  const matchExplanation = useJobMatchExplanation(userId, rawJob?.id ?? null);
  const anonymizedJob = useAnonymizedJob(rawJob ?? NULL_JOB);
  // `job` is what all rendering code uses — masked when blind mode is on,
  // null when no job is selected.
  const job = rawJob ? anonymizedJob : null;
  const { title: displayTitle, description: displayDescription, isTranslating } =
    useJobContentTranslation(job);

  const isMobile = useIsMobile();
  const bodyRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const logClick = useApplyClickLogger();

  // ---- Body scroll lock ---------------------------------------------------
  useEffect(() => {
    if (!job) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [job]);

  // Reset scroll when the job changes
  useEffect(() => {
    bodyRef.current?.scrollTo(0, 0);
  }, [job?.id]);

  // ---- Focus trap ----------------------------------------------------------
  useEffect(() => {
    if (!job) return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = panel.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first?.focus();

    const trap = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    window.addEventListener('keydown', trap);
    return () => window.removeEventListener('keydown', trap);
  }, [job, onClose]);

  // ---- Handlers -----------------------------------------------------------
  const handleApply = useCallback(async () => {
    if (!job || !rawJob) return;
    const clickId = await logClick({ job, kind: 'optimize_and_apply' });
    sessionStorage.setItem(
      'optimize.prefilledJob',
      JSON.stringify({
        // Always persist real company name so the optimizer pre-fill is correct,
        // even when blind mode masks the displayed name.
        jobUrl:        rawJob.url,
        jobTitle:      rawJob.title,
        companyName:   rawJob.company,
        source:        rawJob.source,
        externalJobId: rawJob.external_job_id ?? null,
        clickId:       clickId ?? undefined,
      }),
    );
    onApply(rawJob);
    onClose();
  }, [rawJob, job, logClick, onApply, onClose]);

  const handleView = useCallback(async () => {
    if (!job || !rawJob) return;
    const clickId = await logClick({ job, kind: 'view' });
    sessionStorage.setItem(
      'pendingApplyConfirm',
      JSON.stringify({ clickId, job: rawJob, ts: Date.now() }),
    );
    onView(rawJob);
    window.open(rawJob.url, '_blank', 'noopener,noreferrer');
  }, [rawJob, job, logClick, onView]);

  const handleToggleSave = useCallback(() => {
    if (job) onToggleSave(job);
  }, [job, onToggleSave]);

  // ---- Mobile drag-to-dismiss ---------------------------------------------
  const handleDragEnd = useCallback(
    (_: unknown, info: PanInfo) => {
      if (info.offset.y > DRAG_CLOSE_THRESHOLD) onClose();
    },
    [onClose],
  );

  // ---- Derived values -----------------------------------------------------
  // Memoize the description normalization + section split BEFORE the early
  // return so the hook order stays stable across renders.
  const normalizedDescription = useMemo(
    () => normalizeDescription(displayDescription),
    [displayDescription],
  );
  const sections = useMemo<MarkdownSection[]>(
    () => splitMarkdownSections(normalizedDescription, job?.company ?? ''),
    [normalizedDescription, job?.company],
  );
  const salaryIntel = useMemo(
    () => (rawJob ? computeSalaryIntelligenceForJob(rawJob, profile) : null),
    [rawJob, profile],
  );

  if (!job) return null;
  const currency = job.salary_currency ?? 'INR';
  const numericSalary = buildSalaryLabel(job.salary_min, job.salary_max, currency, t);
  const compensationLabel = job.compensation_text ?? numericSalary;
  const postedIso = job.posted_at ?? job.created;
  const postedLabel = freshnessLabel(postedIso, t, i18n.language);
  const locationText = job.location || (job.remote ? t('dashboard.jobs.jobCard.remote', 'Remote') : '—');
  const durationText = job.duration ?? (job.job_type ?? '—');
  const notSpecified = t('dashboard.jobs.jobDrawer.notSpecified', 'Not specified');
  const notDisclosed = t('dashboard.jobs.jobDrawer.notDisclosed', 'Not disclosed');

  const mustHave = Array.isArray(job.must_have_skills) ? job.must_have_skills : [];
  const goodToHave = Array.isArray(job.good_to_have_skills) ? job.good_to_have_skills : [];
  const hasSplit = mustHave.length > 0 || goodToHave.length > 0;
  const unifiedSkills = Array.isArray(job.skills) ? job.skills : [];
  const profileMatchedSkills = job.matchedSkills ?? [];

  // ---- Animation variants -------------------------------------------------
  const backdrop = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const panel = isMobile
    ? {
        initial: { y: '100%' },
        animate: { y: 0 },
        exit: { y: '100%' },
        transition: { type: 'spring' as const, stiffness: 320, damping: 28 },
      }
    : {
        initial: { x: '100%' },
        animate: { x: 0 },
        exit: { x: '100%' },
        transition: { type: 'spring' as const, stiffness: 320, damping: 28 },
      };

  // ---- Render -------------------------------------------------------------
  return createPortal(
    <AnimatePresence>
      {job && (
        <motion.div
          key="drawer-backdrop"
          {...backdrop}
          className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm"
          onClick={onClose}
          style={{ pointerEvents: 'all' }}
        >
          <motion.div
            ref={panelRef}
            key="drawer-panel"
            {...panel}
            drag={isMobile ? 'y' : false}
            dragConstraints={{ top: 0 }}
            dragElastic={0.15}
            onDragEnd={isMobile ? handleDragEnd : undefined}
            onClick={(e) => e.stopPropagation()}
            className={[
              'ds-job-detail-drawer absolute flex flex-col ds-card-pop p-0 overflow-hidden',
              isMobile
                ? 'bottom-0 inset-x-0 rounded-t-2xl max-h-[85vh]'
                : 'top-0 right-0 bottom-0 w-full max-w-[420px] border-l',
            ].join(' ')}
            style={
              isMobile
                ? { paddingBottom: 'env(safe-area-inset-bottom)' }
                : { borderColor: 'var(--ds-border-strong)' }
            }
          >
            {/* Drag handle (mobile only) */}
            {isMobile && (
              <div className="flex-shrink-0 flex justify-center pt-2 pb-1">
                <div className="w-10 h-1 rounded-full bg-[var(--ds-text-muted)]/40" />
              </div>
            )}

            {/* ── Profile header — gradient wash in dark theme ── */}
            <div className="ds-job-drawer-header flex-shrink-0 border-b" style={{ borderColor: 'var(--ds-border-strong)' }}>

              {/* Banner + avatar wrapper — overflow-visible so avatar isn't clipped */}
              <div className="relative ds-job-drawer-header__banner" style={{ height: 80 }}>
                {/* Close / Back + Save — header actions */}
                <div className="absolute top-2 right-2 flex items-center gap-1 z-10">
                  <button
                    type="button"
                    onClick={handleToggleSave}
                    aria-label={isSaved ? t('dashboard.jobs.jobDrawer.removeSaved', 'Remove from saved') : t('dashboard.jobs.jobCard.saveJob', 'Save job')}
                    className="ds-btn ds-btn-secondary ds-btn-sm rounded-full ds-job-drawer-save-btn"
                    style={{ color: isSaved ? 'var(--ds-brand)' : 'var(--ds-text-muted)' }}
                  >
                    <Bookmark
                      className="w-4 h-4"
                      fill={isSaved ? 'currentColor' : 'none'}
                      strokeWidth={1.75}
                    />
                  </button>
                  {isMobile && (
                    <button type="button" onClick={onClose} aria-label={t('dashboard.jobs.jobDrawer.back', 'Back')}
                      className="ds-btn ds-btn-secondary ds-btn-sm rounded-full">
                      <ArrowLeft className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {!isMobile && (
                    <button type="button" onClick={onClose} aria-label={t('layout.close', 'Close')}
                      className="ds-btn ds-btn-secondary ds-btn-sm rounded-full">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {/* Avatar — sits at bottom of banner, sticking 50% below → no clip */}
                <div
                  className="absolute left-5 bottom-0 translate-y-1/2 z-10
                                w-[68px] h-[68px] rounded-full border-[3px] shadow-md
                                flex items-center justify-center t-hero overflow-hidden"
                  style={{
                    background: 'var(--ds-brand-soft)',
                    color: 'var(--ds-brand)',
                    borderColor: 'var(--ds-bg-card)',
                  }}
                >
                  {job.company_logo_url ? (
                    <img src={job.company_logo_url} alt={job.company}
                      className="w-full h-full object-contain"
                      style={{ background: 'var(--ds-bg-card)' }}
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                  ) : (
                    (job.company?.trim()[0] ?? '?').toUpperCase()
                  )}
                </div>
              </div>

              {/* Identity block — pt accounts for avatar overlap (34px = half of 68) */}
              <div className="px-5 pb-4 pt-[42px]">
                {/* Job title + company + status dot */}
                <div className="flex items-start gap-2 flex-wrap">
                  <h2 className="t-h2">
                    {displayTitle}
                  </h2>
                  {isTranslating && (
                    <span className="t-tag t-muted w-full">
                      {t('dashboard.jobs.jobDrawer.translatingContent', 'Translating job details…')}
                    </span>
                  )}
                  {statusBadge}
                </div>

                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="t-body">
                    {job.company}
                  </span>
                  {/* green online-style dot */}
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--ds-ok)] shrink-0" />
                  {/* Source badge */}
                  {job.source_channel === 'linkedin' ? (
                    <span className="ds-chip ds-chip-info">
                      <svg viewBox="0 0 24 24" width="8" height="8" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                      LinkedIn
                    </span>
                  ) : job.source_channel === 'naukri' ? (
                    <span className="ds-chip ds-chip-warn">Naukri</span>
                  ) : job.source === 'peerlist' ? (
                    <span className="ds-chip ds-chip-ok">
                      <PeerlistIcon size={8} /> Peerlist
                    </span>
                  ) : job.source_channel ? (
                    <span className="ds-chip">
                      {job.source_channel}
                    </span>
                  ) : null}
                </div>

                {/* Posted date */}
                {postedIso && (
                  <div className="t-meta flex items-center gap-1 mt-1.5">
                    <Timer className="w-3 h-3 shrink-0" />
                    {postedLabel} · {new Date(postedIso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                )}
              </div>
            </div>

            {/* Scrollable body — sticky action bar stays visible while scrolling */}
            <div
              ref={bodyRef}
              className="flex-1 min-h-0 overflow-y-auto"
            >
              <div className="ds-job-drawer-action-bar px-4 sm:px-5 py-3 space-y-2">
                <button
                  type="button"
                  onClick={handleApply}
                  className="ds-btn ds-btn-primary w-full min-h-[44px] touch-manipulation"
                >
                  <Zap className="w-4 h-4 shrink-0" />
                  {t('dashboard.jobs.optimizeAndApply', 'Optimize & Apply')}
                </button>
                {rawJob ? (
                  <ShouldIApplyPanel
                    job={rawJob}
                    profile={profile}
                    userId={userId}
                    explanation={matchExplanation.data}
                    explanationStatus={matchExplanation.status}
                    onRefreshExplanation={matchExplanation.refresh}
                  />
                ) : null}
              </div>

              <div className="px-4 sm:px-5 pb-4 pt-2 space-y-5">
              {/* ── At-a-glance meta pills (uniform 2×2 grid) ── */}
              <div className="ds-job-drawer-meta-grid">
                <MetaPill icon={<MapPin className="w-3.5 h-3.5" />} label={locationText} />
                <MetaPill
                  icon={<Briefcase className="w-3.5 h-3.5" />}
                  label={job.job_type || durationText || notSpecified}
                />
                <MetaPill
                  icon={<DollarSign className="w-3.5 h-3.5" />}
                  label={compensationLabel || notDisclosed}
                />
                <MetaPill
                  icon={<Timer className="w-3.5 h-3.5" />}
                  label={job.experience_level || job.duration || notSpecified}
                />
              </div>

              {salaryIntel?.hasData ? (
                <SalaryIntelligenceCard intel={salaryIntel} />
              ) : null}

              {/* Applicants — optional row below meta grid */}
              {job.applicant_count != null && (
                <div className="ds-card ds-job-drawer-panel">
                  <div className="flex items-center justify-between">
                    <div className="t-label flex items-center gap-1.5">
                      <Briefcase className="w-3 h-3" />
                      <span>{t('dashboard.jobs.jobDrawer.applicantsSoFar', 'Applicants so far')}</span>
                    </div>
                    <span className="t-card-title t-num" style={{ color: job.applicant_count > 200 ? 'var(--ds-danger)' : job.applicant_count > 50 ? 'var(--ds-warn)' : 'var(--ds-ok)' }}>
                      {job.applicant_count.toLocaleString()}
                    </span>
                  </div>
                  <div className="ds-progress mt-1.5">
                    <div className="h-full rounded-full transition-all"
                      style={{
                        width: `${Math.min(100, (job.applicant_count / 500) * 100)}%`,
                        background: job.applicant_count > 200 ? 'var(--ds-danger)' : job.applicant_count > 50 ? 'var(--ds-warn)' : 'var(--ds-ok)',
                      }} />
                  </div>
                  <p className="t-tag t-muted mt-1">
                    {job.applicant_count > 200 ? t('dashboard.jobs.jobDrawer.highCompetition', 'High competition — apply quickly') :
                     job.applicant_count > 50 ? t('dashboard.jobs.jobDrawer.moderateCompetition', 'Moderate competition') :
                     t('dashboard.jobs.jobDrawer.lowCompetition', 'Low competition — great time to apply')}
                  </p>
                </div>
              )}

              {/* ── Skills — core + collapsible secondary, matched highlight ── */}
              {hasSplit ? (
                <div className="space-y-5">
                  <CollapsibleSkillGroup
                    title={t('dashboard.jobs.jobDrawer.mustHaveSkills', 'Must have skills')}
                    skills={mustHave}
                    matchedSkills={profileMatchedSkills}
                  />
                  <CollapsibleSkillGroup
                    title={t('dashboard.jobs.jobDrawer.goodToHaveSkills', 'Good to have skills')}
                    skills={goodToHave}
                    matchedSkills={profileMatchedSkills}
                  />
                </div>
              ) : (
                unifiedSkills.length > 0 && (
                  <CollapsibleSkillGroup
                    title={t('dashboard.jobs.jobDrawer.requiredSkills', 'Required skills')}
                    skills={unifiedSkills}
                    matchedSkills={profileMatchedSkills}
                  />
                )
              )}

              {/* ── About this Opportunity ──────────────────────────────
                 Splits the markdown by `## / ### ` headings so each natural
                 section (Job Description, Responsibilities, Qualifications,
                 Required Education/Skills, …) is its own bordered block. */}
              {sections.length === 0 ? (
                <div className="flex flex-col gap-3">
                  <p className="t-meta italic">
                    {t('dashboard.jobs.jobDrawer.noDescription', 'No description was captured for this job.')}
                  </p>
                  {job.url && job.url !== '#' && (
                    <a
                      href={job.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ds-btn ds-btn-secondary w-fit"
                    >
                      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                      {t('dashboard.jobs.jobDrawer.viewFullOn', {
                        defaultValue: 'View full job on {{site}}',
                        site: job.source_channel === 'linkedin' ? 'LinkedIn' : job.source_channel === 'naukri' ? 'Naukri' : t('dashboard.jobs.jobDrawer.jobSite', 'job site'),
                      })}
                    </a>
                  )}
                </div>
              ) : (
                (() => {
                  const opportunity = sections.filter(s => s.bucket === 'opportunity');
                  const aboutUs = sections.filter(s => s.bucket === 'about_us');
                  return (
                    <>
                      {opportunity.length > 0 && (
                        <div className="space-y-4">
                          <h3 className="t-h3">
                            {t('dashboard.jobs.jobDrawer.aboutOpportunity', 'About this Opportunity')}
                          </h3>
                          {/* Preamble block (text before the first heading) */}
                          {opportunity
                            .filter(s => s.level === 0 && s.body.trim())
                            .map((s, i) => (
                              <div key={`preamble-${i}`} className={PROSE_CLASSES}>
                                <ReactMarkdown>{s.body}</ReactMarkdown>
                              </div>
                            ))}
                          {/* Headed sections — h2 renders as its own card,
                             h3 renders nested under whichever card came before */}
                          {opportunity
                            .filter(s => s.level !== 0)
                            .map((s, i) => (
                              <Section
                                key={`opp-${i}-${s.title}`}
                                title={localizeSectionTitle(s.title, t, job.company)}
                                body={s.body}
                                nested={s.level === 3}
                              />
                            ))}
                        </div>
                      )}

                      {aboutUs.length > 0 && (
                        <div className="space-y-4 pt-2">
                          <h3 className="t-h3">
                            {t('dashboard.jobs.jobDrawer.aboutCompany', {
                              defaultValue: 'About {{company}}',
                              company: aboutUs[0]?.title?.startsWith('About ')
                                ? aboutUs[0].title.replace(/^About\s+/i, '')
                                : job.company,
                            })}
                          </h3>
                          {aboutUs.map((s, i) => (
                            <Section
                              key={`about-${i}-${s.title}`}
                              title={
                                // Hide the h4 when the heading basically says
                                // "About <Company>" — we already put it in h3.
                                /^about\s+/i.test(s.title)
                                  ? ''
                                  : localizeSectionTitle(s.title, t, job.company)
                              }
                              body={s.body}
                              nested={s.level === 3}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()
              )}

              {/* ── Why this job for you? (TASK 3-6) ──────────────────────
                 Loaded async below the fold. Does NOT block drawer render.
                 Renders skeleton while the explanation is fetching. */}
              <WhyThisJob
                jobId={job.id}
                matchScore={job.matchScore}
                explanationStatus={matchExplanation.status}
                data={matchExplanation.data}
                refresh={matchExplanation.refresh}
              />

              {onSelectSimilar && allJobs.length > 0 && (
                <SimilarJobs
                  currentJob={job}
                  allJobs={allJobs}
                  onSelect={onSelectSimilar}
                />
              )}

              <p className="t-tag t-muted pt-3 border-t" style={{ borderColor: 'var(--ds-border-strong)' }}>
                {t('dashboard.jobs.jobDrawer.source', 'Source')} ·{' '}
                <a
                  href={job.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="t-link"
                >
                  {job.source_channel === 'linkedin' ? t('dashboard.jobs.jobDrawer.viewOnLinkedIn', 'View on LinkedIn') :
                   job.source_channel === 'naukri' ? t('dashboard.jobs.jobDrawer.viewOnNaukri', 'View on Naukri') :
                   job.source === 'peerlist' ? t('dashboard.jobs.jobDrawer.viewOnPeerlist', 'View on Peerlist') :
                   t('dashboard.jobs.jobDrawer.viewPosting', 'View job posting')}
                </a>
              </p>
              </div>
            </div>

            {/* Slim footer — just the external apply link */}
            <div
              className="flex-shrink-0 border-t px-5 py-2.5 flex items-center justify-center"
              style={{ borderColor: 'var(--ds-border-strong)', background: 'var(--ds-bg-recessed)' }}
            >
              <button
                type="button"
                onClick={handleView}
                className="ds-btn ds-btn-link"
              >
                <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                {job.source_channel === 'linkedin' ? t('dashboard.jobs.jobDrawer.applyOnLinkedIn', 'Apply on LinkedIn') :
                 job.source_channel === 'naukri' ? t('dashboard.jobs.jobDrawer.applyOnNaukri', 'Apply on Naukri') :
                 job.source === 'peerlist' ? t('dashboard.jobs.jobDrawer.applyOnPeerlist', 'Apply on Peerlist') :
                 t('dashboard.jobs.jobDrawer.viewPosting', 'View job posting')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default React.memo(JobDetailDrawer);
